import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, X, UserPlus, FileText, ExternalLink } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Toast } from '../../components/ui/Toast';
import { api } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { Prospect, Application } from '../../types';
import { DocumentViewer } from '../../components/documents/DocumentViewer';
import { isPublicSolicitudContestada } from '../../utils/solicitudNotes';
import { formatCurrencyMxn, resolveMonthlyRentForDisplay } from '../../utils/money';
import {
  buildSolicitudFormDisplayState,
  formatDateSpanishLong,
  formatDateTimeSpanishLong,
  formatDatoForField,
  formatPhoneDisplay,
  getDocDisplayName,
  getDocDownloadUrl,
  getDocMime,
  isDocImage,
  isDocPdf,
  labelizeKey,
} from '../../utils/solicitudFormDisplay';

type ProspectDetailTab = 'general' | 'informacion' | 'documentos';

function mapApiProspectToProspect(item: any): Prospect {
  const contactDate = item.contactDate
    ? (typeof item.contactDate === 'string' ? item.contactDate : new Date(item.contactDate).toISOString().split('T')[0])
    : '';
  return {
    id: item.id,
    name: item.name,
    email: item.email,
    phone: item.phone,
    propertyId: item.propertyId,
    propertyName: item.properties?.name,
    contactDate,
    status: item.status || 'new',
    approvalStatus: item.approvalStatus ?? null,
    notes: item.notes,
  };
}

function mapApiApplicationToApp(item: any): Application {
  const submittedDate = item.submittedDate
    ? (typeof item.submittedDate === 'string' ? item.submittedDate : new Date(item.submittedDate).toISOString().split('T')[0])
    : '';
  return {
    id: item.id,
    prospectId: item.prospectId,
    prospectName: item.prospects?.name,
    prospectEmail: item.prospects?.email ?? '',
    prospectPhone: item.prospects?.phone ?? '',
    propertyId: item.propertyId,
    propertyName: item.properties?.name,
    unitNumber: item.unitNumber,
    submittedDate,
    status: item.status,
    notes: item.notes,
    solicitudContestada: item.solicitudContestada === true,
  };
}

export const ProspectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState<Prospect | null>(null);
  const [prospectApplication, setProspectApplication] = useState<Application | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [hasTenant, setHasTenant] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<ProspectDetailTab>('general');

  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      setLoading(true);
      setHasTenant(null);
      const [prospectData, applicationsRes] = await Promise.all([
        api.getProspect(id),
        api.getApplications({ limit: 500 }),
      ]);
      const prospectMapped = mapApiProspectToProspect(prospectData);
      setProspect(prospectMapped);
      const applicationsList = applicationsRes?.data ?? [];
      const apps = (applicationsList || []).map(mapApiApplicationToApp);
      const appFromList = apps.find((a) => a.prospectId === id);
      // El listado no trae `notes` (payload liviano). Sin notes no hay formulario ni documentos en tabs.
      let app = appFromList ?? null;
      if (appFromList) {
        try {
          const full = await api.getApplication(appFromList.id);
          app = mapApiApplicationToApp(full);
        } catch {
          app = appFromList;
        }
      }
      setProspectApplication(app);
      if (prospectMapped.approvalStatus === 'approved' && app) {
        const tenantsRes = await api.getTenants({ limit: 500 });
        const tenants = tenantsRes?.data ?? [];
        const exists = tenants.some(
          (t: any) =>
            t.propertyId === app.propertyId &&
            t.unitNumber === app.unitNumber &&
            (t.email === prospectMapped.email || t.name === prospectMapped.name),
        );
        setHasTenant(exists);
      }
    } catch (err: any) {
      setError(err.message || 'Error al cargar el prospecto');
      setProspect(null);
      setProspectApplication(null);
      setHasTenant(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const { data: unitsRes } = useQuery({
    queryKey: ['prospect-detail-units', prospectApplication?.propertyId],
    queryFn: async () => {
      if (!prospectApplication?.propertyId) return [];
      return api.getUnits(prospectApplication.propertyId);
    },
    enabled: !!prospectApplication?.propertyId,
    staleTime: 60_000,
  });

  const unit = useMemo(() => {
    if (!prospectApplication) return null;
    const list = Array.isArray(unitsRes) ? unitsRes : (unitsRes as any)?.data ?? [];
    return list.find((u: any) => u.unitNumber === prospectApplication.unitNumber) || null;
  }, [prospectApplication, unitsRes]);

  const formDisplay = useMemo(
    () => buildSolicitudFormDisplayState(prospectApplication?.notes),
    [prospectApplication?.notes],
  );
  const { parsedNotes, datosFormulario, orderedKeys, docKeysWithValue } = formDisplay;

  const solicitudContestada = isPublicSolicitudContestada(prospectApplication?.notes);

  const handleApprove = async () => {
    if (!prospect) return;
    try {
      setActionLoading(true);
      await api.updateProspect(prospect.id, { approvalStatus: 'approved' });
      if (prospectApplication) {
        await api.updateApplication(prospectApplication.id, { status: 'approved' });
        const tenantsRes = await api.getTenants({ limit: 500 });
        const existingTenants = tenantsRes?.data ?? [];
        const alreadyTenant = existingTenants.some(
          (t: any) =>
            t.propertyId === prospectApplication.propertyId &&
            t.unitNumber === prospectApplication.unitNumber &&
            (t.email === prospect.email || t.name === prospect.name),
        );
        if (!alreadyTenant) {
          await createTenantFromProspect();
        }
      }
      setToast({ message: 'Solicitud aprobada. El inquilino ya aparece en Inquilinos.', type: 'success' });
      await loadData();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al aprobar', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!prospect) return;
    try {
      setActionLoading(true);
      await api.updateProspect(prospect.id, { approvalStatus: 'rejected' });
      if (prospectApplication) {
        await api.updateApplication(prospectApplication.id, { status: 'rejected' });
      }
      setToast({ message: 'Solicitud rechazada', type: 'success' });
      await loadData();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al rechazar', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const createTenantFromProspect = useCallback(async () => {
    if (!prospect || !prospectApplication) return;
    const monthlyRent = resolveMonthlyRentForDisplay(unit, prospectApplication.notes) ?? 0;
    const startDate = new Date();
    const endDate = new Date();
    endDate.setFullYear(endDate.getFullYear() + 1);
    await api.createTenant({
      name: prospect.name,
      email: prospect.email,
      phone: prospect.phone || '',
      propertyId: prospectApplication.propertyId,
      unitNumber: prospectApplication.unitNumber,
      monthlyRent,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      status: 'active',
      prospectId: prospect.id,
    });
  }, [prospect, prospectApplication, unit]);

  const handleAddToTenants = async () => {
    if (!prospect || !prospectApplication) return;
    try {
      setActionLoading(true);
      await createTenantFromProspect();
      setToast({ message: 'Inquilino creado. Ya aparece en Inquilinos.', type: 'success' });
      await loadData();
    } catch (err: any) {
      setToast({ message: err.message || 'Error al crear inquilino', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const monthlyRentDisplay = useMemo(
    () => resolveMonthlyRentForDisplay(unit, prospectApplication?.notes),
    [unit, prospectApplication?.notes],
  );

  const submittedDateLabel = useMemo(() => {
    if (!prospectApplication?.submittedDate) return '—';
    return formatDateSpanishLong(new Date(prospectApplication.submittedDate));
  }, [prospectApplication?.submittedDate]);

  if (loading && !prospect) {
    return (
      <div className="flex justify-center py-12 text-gray-500">Cargando prospecto...</div>
    );
  }

  if (error && !prospect) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
        <Button onClick={() => navigate('/rentas/prospectos')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  if (!prospect) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Prospecto no encontrado</p>
        <Button onClick={() => navigate('/rentas/prospectos')} className="mt-4">
          Volver a la lista
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" icon={ArrowLeft} onClick={() => navigate('/rentas/prospectos')}>
            Volver
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">{prospect.name}</h1>
        </div>
        <span className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">
          Prospecto
        </span>
      </div>

      <div className="border-b border-gray-200">
        <nav className="flex flex-wrap gap-x-8 gap-y-1">
          {(
            [
              { id: 'general' as const, label: 'General' },
              { id: 'informacion' as const, label: 'Información' },
              { id: 'documentos' as const, label: 'Documentos' },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-accent text-accent'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'general' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card title="Información del prospecto">
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-gray-500">Nombre</div>
                <div className="font-medium text-gray-900">{prospect.name}</div>
              </div>
              <div>
                <div className="text-gray-500">Email</div>
                <div className="font-medium text-gray-900">{prospect.email}</div>
              </div>
              <div>
                <div className="text-gray-500">Teléfono</div>
                <div className="font-medium text-gray-900">
                  {prospect.phone ? formatPhoneDisplay(String(prospect.phone)) : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Propiedad de interés</div>
                <div className="font-medium text-gray-900">{prospect.propertyName ?? '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Fecha de contacto</div>
                <div className="font-medium text-gray-900">
                  {prospect.contactDate
                    ? formatDateSpanishLong(new Date(prospect.contactDate))
                    : '—'}
                </div>
              </div>
              <div>
                <div className="text-gray-500">Estado de aprobación</div>
                <div className="font-medium text-gray-900">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      prospect.approvalStatus === 'approved'
                        ? 'bg-green-100 text-green-800'
                        : prospect.approvalStatus === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {prospect.approvalStatus === 'approved'
                      ? 'Aprobado'
                      : prospect.approvalStatus === 'rejected'
                      ? 'Desaprobado'
                      : 'Pendiente'}
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Resumen de la solicitud">
            {prospectApplication ? (
              <div className="space-y-4 text-sm">
                <div>
                  <div className="text-gray-500">Propiedad</div>
                  <div className="font-medium text-gray-900">{prospectApplication.propertyName ?? '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Unidad</div>
                  <div className="font-medium text-gray-900">{prospectApplication.unitNumber}</div>
                </div>
                <div>
                  <div className="text-gray-500">Monto mensual</div>
                  <p className="text-2xl font-bold text-primary">
                    {canViewPrices && monthlyRentDisplay != null
                      ? formatCurrencyMxn(monthlyRentDisplay)
                      : '—'}
                  </p>
                </div>
                <div>
                  <div className="text-gray-500">Fecha de solicitud</div>
                  <div className="font-medium text-gray-900">{submittedDateLabel}</div>
                </div>
                <div>
                  <div className="text-gray-500">Estado</div>
                  <div className="font-medium text-gray-900">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        prospectApplication.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : prospectApplication.status === 'rejected'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}
                    >
                      {prospectApplication.status === 'approved'
                        ? 'Aprobada'
                        : prospectApplication.status === 'rejected'
                        ? 'Rechazada'
                        : 'Pendiente'}
                    </span>
                  </div>
                </div>
                {solicitudContestada && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={() => navigate(`/rentas/solicitudes/${prospectApplication.id}`)}
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Abrir vista completa de la solicitud
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-sm">No hay solicitud asociada a este prospecto</p>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'informacion' && (
        <Card title="Información del formulario">
          <div className="space-y-4 text-sm">
            {!prospectApplication ? (
              <p className="text-gray-500">No hay solicitud asociada; no hay formulario público que mostrar.</p>
            ) : parsedNotes ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  {solicitudContestada && (
                    <button
                      type="button"
                      onClick={() => navigate(`/rentas/solicitudes/${prospectApplication.id}`)}
                      className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Ver en Solicitudes
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <div className="text-gray-500">Tipo de persona</div>
                    <div className="font-medium text-gray-900">{parsedNotes?.tipoPersona || '—'}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Guardado</div>
                    <div className="font-medium text-gray-900">
                      {parsedNotes?.savedAt ? formatDateTimeSpanishLong(String(parsedNotes.savedAt)) : '—'}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2">Contacto</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <div className="text-gray-500">Nombre</div>
                      <div className="font-medium text-gray-900">{parsedNotes?.contacto?.name || '—'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Correo</div>
                      <div className="font-medium text-gray-900">{parsedNotes?.contacto?.email || '—'}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Teléfono</div>
                      <div className="font-medium text-gray-900">
                        {parsedNotes?.contacto?.phone ? formatPhoneDisplay(String(parsedNotes.contacto.phone)) : '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="font-semibold text-gray-900 mb-2">Datos del formulario</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {orderedKeys.map((key) => {
                      if (!Object.prototype.hasOwnProperty.call(datosFormulario, key)) return null;
                      const value = datosFormulario[key];
                      const isEmpty =
                        value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
                      if (isEmpty) return null;
                      return (
                        <div key={key} className="break-words">
                          <div className="text-gray-500">{labelizeKey(key)}</div>
                          <div className="font-medium text-gray-900">{formatDatoForField(key, value)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <p className="text-gray-600 mb-3">
                  {solicitudContestada
                    ? 'No se pudo leer el formulario en formato esperado.'
                    : 'El prospecto aún no ha enviado el formulario público de solicitud. Cuando lo haga, la información aparecerá aquí.'}
                </p>
                {prospectApplication.notes && (
                  <>
                    <div className="text-gray-500 text-sm mb-2">Notas en la solicitud (texto interno)</div>
                    <pre className="whitespace-pre-wrap break-words text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-96 overflow-auto">
                      {prospectApplication.notes}
                    </pre>
                  </>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {activeTab === 'documentos' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-lg font-semibold">Documentos</h2>
              <p className="text-sm text-gray-500 mt-1">Documentos adjuntos a la solicitud de este prospecto</p>
            </div>
            {solicitudContestada && prospectApplication && (
              <button
                type="button"
                onClick={() => navigate(`/rentas/solicitudes/${prospectApplication.id}`)}
                className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
              >
                <ExternalLink className="w-4 h-4" />
                Ver en Solicitudes
              </button>
            )}
          </div>
          <Card>
            {!prospectApplication ? (
              <p className="text-gray-500 text-sm py-8 text-center">No hay solicitud asociada.</p>
            ) : docKeysWithValue.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">
                  {solicitudContestada
                    ? 'No hay documentos adjuntos en el formulario.'
                    : 'Los documentos aparecerán cuando el prospecto envíe el formulario público.'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docKeysWithValue.map((key) => {
                  const value = datosFormulario[key];
                  const viewUrl = getDocDownloadUrl(value);
                  const displayName = getDocDisplayName(key, value);
                  const mime = getDocMime(value);
                  const isPdf = isDocPdf(value);
                  const isImage = isDocImage(value);
                  const kindLabel = isImage ? 'IMAGEN' : isPdf ? 'PDF' : mime ? mime.split('/').pop()?.toUpperCase() || 'DOC' : 'DOC';
                  const displayDate = parsedNotes?.savedAt
                    ? formatDateSpanishLong(new Date(parsedNotes.savedAt))
                    : prospectApplication.submittedDate
                    ? formatDateSpanishLong(new Date(prospectApplication.submittedDate))
                    : '—';

                  if (!viewUrl) return null;
                  return (
                    <DocumentViewer
                      key={key}
                      title={displayName || labelizeKey(key)}
                      fileUrl={viewUrl}
                      fileTypeLabel={`${kindLabel} · ${displayDate}`}
                      downloadName={displayName || undefined}
                    />
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      )}

      {prospect.approvalStatus !== 'approved' && prospect.approvalStatus !== 'rejected' && (
        <Card title="Acciones">
          <div className="flex items-center space-x-4">
            <p className="text-sm text-gray-600">Puedes aprobar o desaprobar esta solicitud</p>
            <div className="flex space-x-3">
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="inline-flex items-center justify-center px-4 py-2 bg-green-500 text-white font-medium rounded-lg hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-5 h-5 mr-2" />
                {actionLoading ? 'Guardando...' : 'Aprobar'}
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="inline-flex items-center justify-center px-4 py-2 bg-red-500 text-white font-medium rounded-lg hover:bg-red-600 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X className="w-5 h-5 mr-2" />
                Desaprobar
              </button>
            </div>
          </div>
        </Card>
      )}

      {(prospect.approvalStatus === 'approved' || prospect.approvalStatus === 'rejected') && (
        <Card>
          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`px-3 py-2 rounded-lg text-sm font-medium ${
                prospect.approvalStatus === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}
            >
              Esta solicitud ya ha sido {prospect.approvalStatus === 'approved' ? 'aprobada' : 'desaprobada'}
            </span>
            {prospect.approvalStatus === 'approved' && prospectApplication && hasTenant === false && (
              <button
                type="button"
                onClick={handleAddToTenants}
                disabled={actionLoading}
                className="inline-flex items-center justify-center px-4 py-2 bg-primary text-white font-medium rounded-lg hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <UserPlus className="w-5 h-5 mr-2" />
                {actionLoading ? 'Creando...' : 'Agregar a Inquilinos'}
              </button>
            )}
          </div>
        </Card>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}
    </div>
  );
};
